import React, { useState } from 'react';
import { MOCK_EXPENSES, EXPENSE_CATEGORIES, formatUGX } from '../mock';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Plus, Wallet, TrendingDown } from 'lucide-react';

const CAT_TONE = {
  Rent: 'bg-blue-50 text-blue-700 border-blue-200',
  Salaries: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Utilities: 'bg-amber-50 text-amber-700 border-amber-200',
  Restocking: 'bg-green-50 text-green-700 border-green-200',
  Transport: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  Licences: 'bg-purple-50 text-purple-700 border-purple-200',
  Marketing: 'bg-pink-50 text-pink-700 border-pink-200',
  Other: 'bg-slate-50 text-slate-700 border-slate-200',
};

const ExpensesPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState(MOCK_EXPENSES);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'Rent', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });

  const add = () => {
    if (!form.description || !form.amount) { toast({ title: 'Missing info', description: 'Description and amount are required.', variant: 'destructive' }); return; }
    setRows((r) => [{ id: 'E-' + Date.now(), ...form, amount: Number(form.amount), paidBy: user?.name || 'You' }, ...r]);
    setForm({ category: 'Rent', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });
    setOpen(false);
    toast({ title: 'Expense recorded', description: `${formatUGX(Number(form.amount))} · ${form.category}` });
  };

  const total = rows.reduce((s, e) => s + e.amount, 0);
  const byCat = EXPENSE_CATEGORIES.map((c) => ({ c, v: rows.filter((r) => r.category === c).reduce((s, r) => s + r.amount, 0) })).filter((x) => x.v > 0).sort((a, b) => b.v - a.v);
  const topCat = byCat[0];

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-slate-900">Expenses</h1>
          <p className="text-slate-500 mt-2 text-[15px]">Record and review pharmacy running costs to keep your books clean.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-11 px-5 font-semibold gap-2"><Plus size={18} /> Add expense</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-xl">Record an expense</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-slate-700">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" /></div>
              </div>
              <div><Label className="text-slate-700">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Monthly rent" className="mt-1.5 h-11" /></div>
              <div><Label className="text-slate-700">Amount (UGX)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" className="mt-1.5 h-11" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={add} className="bg-blue-600 hover:bg-blue-700 text-white">Save expense</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-3 gap-5 mt-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><TrendingDown size={22} /></div>
          <div><div className="font-display font-extrabold text-2xl text-slate-900">{formatUGX(total)}</div><div className="text-slate-500 text-sm">Total expenses</div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Wallet size={22} /></div>
          <div><div className="font-display font-extrabold text-2xl text-slate-900">{rows.length}</div><div className="text-slate-500 text-sm">Entries recorded</div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-slate-500 text-sm">Biggest category</div>
          <div className="font-display font-extrabold text-xl text-slate-900 mt-1">{topCat ? topCat.c : '—'}</div>
          <div className="text-slate-500 text-sm">{topCat ? formatUGX(topCat.v) : ''}</div>
        </div>
      </div>

      {/* By category bars */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6">
        <h3 className="font-display font-bold text-lg text-slate-900 mb-4">Spending by category</h3>
        <div className="space-y-3">
          {byCat.map((x) => (
            <div key={x.c}>
              <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{x.c}</span><span className="font-medium text-slate-800">{formatUGX(x.v)}</span></div>
              <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${(x.v / total) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto mt-6">
        <table className="w-full text-[15px] min-w-[720px]">
          <thead>
            <tr className="text-left text-slate-500 text-sm border-b border-slate-100">
              <th className="px-6 py-3 font-semibold">Date</th>
              <th className="px-6 py-3 font-semibold">Category</th>
              <th className="px-6 py-3 font-semibold">Description</th>
              <th className="px-6 py-3 font-semibold">Paid by</th>
              <th className="px-6 py-3 font-semibold text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-6 py-4 text-slate-600">{e.date}</td>
                <td className="px-6 py-4"><Badge variant="outline" className={CAT_TONE[e.category] || CAT_TONE.Other}>{e.category}</Badge></td>
                <td className="px-6 py-4 text-slate-700">{e.description}</td>
                <td className="px-6 py-4 text-slate-600">{e.paidBy}</td>
                <td className="px-6 py-4 text-right font-semibold text-slate-800">{formatUGX(e.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExpensesPage;
